import React from 'react';
import { Box, Card, CardContent, Skeleton } from '@mui/material';
import { colors } from '@src/styles';

interface GroupsSkeletonProps {
  cardCount?: number;
}

const SkeletonCard: React.FC = () => {
  return (
    <Card
      sx={{
        backgroundColor: colors.background.primary,
        border: `1px solid ${colors.background.secondary}`,
        borderRadius: '8px',
        mb: 2,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            {/* Group name */}
            <Skeleton
              variant="text"
              width="60%"
              height={28}
              sx={{
                backgroundColor: colors.background.secondary,
                mb: 1,
              }}
            />

            {/* Task count and completion percentage */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Skeleton
                variant="rectangular"
                width={80}
                height={24}
                sx={{
                  backgroundColor: colors.background.secondary,
                  borderRadius: '12px',
                }}
              />
              <Skeleton
                variant="text"
                width={100}
                height={20}
                sx={{
                  backgroundColor: colors.background.secondary,
                }}
              />
            </Box>

            {/* Progress bar */}
            <Skeleton
              variant="rectangular"
              width="100%"
              height={6}
              sx={{
                backgroundColor: colors.background.secondary,
                borderRadius: '3px',
              }}
            />
          </Box>

          {/* Expand icon */}
          <Skeleton
            variant="circular"
            width={48}
            height={48}
            sx={{
              backgroundColor: colors.background.secondary,
            }}
          />
        </Box>

        {/* Status chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width={Math.random() * 60 + 80}
              height={24}
              sx={{
                backgroundColor: colors.background.secondary,
                borderRadius: '12px',
              }}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export const GroupsSkeleton: React.FC<GroupsSkeletonProps> = ({ cardCount = 6 }) => {
  return (
    <Box
      sx={{
        height: 'calc(100%)',
        width: '100%',
        overflowY: 'auto',
        pr: 2,
        animation: 'shimmer 2s ease-in-out infinite',
        '@keyframes shimmer': {
          '0%': {
            opacity: 1,
          },
          '50%': {
            opacity: 0.4,
          },
          '100%': {
            opacity: 1,
          },
        },
      }}
    >
      {/* Group cards skeleton */}
      {Array.from({ length: cardCount }).map((_, index) => (
        <SkeletonCard key={index} />
      ))}
    </Box>
  );
};
